import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';

import { constructUrl } from '@/lib/utils';
import {
	SearchResultDocumentModel,
	FormOption,
	searchSortOptions,
	SEARCH_SORT,
	AccountBookmarkModel,
	BOOKMARK_TYPE,
} from '@/lib/models';
import { searchService } from '@/lib/services';
import { useQuery, useHandleError } from '@/hooks';
import { LinkBox, LoadingButton, Select, RecentlyViewed, SearchListItem } from '@/components';

export const Search = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const handleError = useHandleError();

	const query = useQuery();
	const searchQuery = useMemo(() => query.get('query'), [query]);
	const fromQuery = useMemo(() => query.get('from'), [query]);
	const filterQuery = useMemo(() => query.get('filter'), [query]);
	const searchSort = useMemo(() => query.get('searchSort') ?? SEARCH_SORT.RELEVANCE, [query]);

	const [sortingOptions, setSortingOptions] = useState(searchSortOptions);
	const [isLoadingFilters, setIsLoadingFilters] = useState(false);
	const [searchResults, setSearchResults] = useState<SearchResultDocumentModel[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [hasNext, setHasNext] = useState(false);
	const [contentTypeFilters, setContentTypeFilters] = useState<FormOption[]>([]);
	const [loadingSearchResults, setLoadingSearchResults] = useState(false);

	const fetchContentTypes = useCallback(async () => {
		setIsLoadingFilters(true);

		try {
			const response = await searchService.getSearchFilters().fetch();
			setContentTypeFilters(response.searchFilters);
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoadingFilters(false);
		}
	}, [handleError]);

	useEffect(() => {
		fetchContentTypes();
	}, [fetchContentTypes]);

	const fetchResults = useCallback(async () => {
		setLoadingSearchResults(true);

		try {
			const response = await searchService
				.getSearchResults({
					...(filterQuery && { filter: filterQuery }),
					...(searchQuery && { query: searchQuery }),
					...(searchSort && { searchSort }),
					from: fromQuery ?? '0',
				})
				.fetch();

			setTotalCount(response.count ?? 0);
			setHasNext(response.hasNext);
			setSearchResults((previousValue) => {
				const shouldConcat = parseInt(fromQuery ?? '0', 10) > 0;

				if (shouldConcat) {
					return previousValue.concat(response.documents);
				}

				return response.documents;
			});
		} catch (error) {
			handleError(error);
		} finally {
			setLoadingSearchResults(false);
		}
	}, [filterQuery, searchQuery, searchSort, fromQuery, handleError]);

	useEffect(() => {
		fetchResults();
	}, [fetchResults]);

	const determineAvailableSortOptionsForContentTypeId = useCallback(() => {
		const allowDateSortContentTypeIds = ['DOCUMENT', 'EVENT', 'BLOG'];

		if (filterQuery && allowDateSortContentTypeIds.includes(filterQuery)) {
			setSortingOptions(searchSortOptions);
		} else {
			setSortingOptions(
				searchSortOptions.filter(
					({ searchSort }) => searchSort !== SEARCH_SORT.DATE_ASC && searchSort !== SEARCH_SORT.DATE_DESC
				)
			);
		}
	}, [filterQuery]);

	useEffect(() => {
		determineAvailableSortOptionsForContentTypeId();
	}, [determineAvailableSortOptionsForContentTypeId, filterQuery]);

	const handleSortBySelectChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		navigate(constructUrl(pathname, { searchSort: currentTarget.value, from: '0' }, search), { replace: true });
	};

	const handleContentTypeSelectChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		navigate(
			constructUrl(
				pathname,
				{ filter: currentTarget.value, searchSort: SEARCH_SORT.RELEVANCE, from: '0' },
				search
			),
			{ replace: true }
		);
	};

	const handleClearButtonClick = () => {
		navigate(constructUrl(pathname, { filter: '', searchSort: '', from: '0' }, search), { replace: true });
	};

	const handleLoadMoreButtonClick = () => {
		navigate(constructUrl(pathname, { from: `${searchResults.length}` }, search), { replace: true });
	};

	const handleListItemChange = useCallback((bookmark: AccountBookmarkModel) => {
		const bookmarkFlagValue = bookmark.bookmarkContent.bookmarkedFlag;

		setSearchResults((previousValue) => {
			const originalResult = previousValue.find(
				(searchResult) => searchResult.id === bookmark.bookmarkContent.documentId
			);

			if (!originalResult) {
				return previousValue;
			}

			return previousValue.map((d) =>
				d.id === originalResult.id ? { ...originalResult, bookmarkFlag: bookmarkFlagValue } : d
			);
		});
	}, []);

	return (
		<>
			<Helmet>
				<title>Hackett Connect - Search</title>
			</Helmet>

			<Container className="pt-7">
				<Row>
					<Col>
						<div className="d-flex align-items-center justify-content-between">
							<h1 className="mb-0 text-primary">
								{loadingSearchResults ? (
									'Loading...'
								) : (
									<>
										{searchQuery
											? totalCount === 1
												? `${totalCount} Result for "${searchQuery}"`
												: `${totalCount} Results for "${searchQuery}"`
											: `${totalCount} Results`}
									</>
								)}
							</h1>
						</div>
					</Col>
				</Row>
			</Container>

			<Container className="pt-7 pb-16">
				<Row>
					<Col xs={12} xl={8} className="mb-6 pr-1">
						<Row className="mb-6">
							<Col>
								<div className="d-flex align-items-center justify-content-end">
									<Form.Label className="mb-0 text-muted">Sort By</Form.Label>
									<Select variant="secondary" value={searchSort} onChange={handleSortBySelectChange}>
										{sortingOptions.map((option) => (
											<option key={option.searchSort} value={option.searchSort}>
												{option.title}
											</option>
										))}
									</Select>
								</div>
							</Col>
						</Row>

						{!loadingSearchResults && searchResults.length <= 0 ? (
							<p className="mb-2 text-muted text-center">
								<strong>No Results</strong>
							</p>
						) : (
							<>
								{searchResults.map((searchItem) => (
									<SearchListItem
										id={searchItem.id}
										key={searchItem.id}
										tags={searchItem.topicTags}
										category={searchItem.contentTypes}
										title={searchItem.title}
										description={searchItem.description}
										detailsUrl={searchItem.detailsUrl}
										downloadUrl={searchItem.downloadUrl}
										viewUrl={searchItem.viewUrl}
										canDownload={searchItem.canDownload}
										type={searchItem.type}
										isBookmarked={searchItem.bookmarkFlag ?? false}
										bookmarkType={BOOKMARK_TYPE.DOCUMENT}
										onChange={handleListItemChange}
										isPodcast={!!searchItem.podcastUrl}
									/>
								))}

								{searchResults.length > 0 && hasNext && (
									<Row>
										<Col xs={12} xl={12} className="d-flex justify-content-center mt-5">
											<LoadingButton
												isLoading={loadingSearchResults}
												variant="outline-dark"
												onClick={handleLoadMoreButtonClick}
											>
												Load More
											</LoadingButton>
										</Col>
									</Row>
								)}
							</>
						)}
					</Col>

					<Col xs={12} xl={4} className="mb-6">
						<Card className="mb-2">
							<Card.Body>
								<div className="mb-4 d-flex align-items-end justify-content-between">
									<h3 className="mb-0">Filter by type</h3>
									<Button variant="link" onClick={handleClearButtonClick}>
										Clear
									</Button>
								</div>
								<Form.Group className="mb-6">
									<Select
										value={filterQuery ?? ''}
										onChange={handleContentTypeSelectChange}
										disabled={isLoadingFilters}
									>
										<option value="">All</option>;
										{contentTypeFilters.map((typeFilter) => {
											return (
												<option key={typeFilter.id} value={typeFilter.id}>
													{typeFilter.display}
												</option>
											);
										})}
									</Select>
								</Form.Group>
							</Card.Body>
						</Card>
						<RecentlyViewed className="mb-2" />
						<LinkBox
							sections={[
								{
									title: "Can't find what you're looking for?",
									links: [{ title: 'Contact Hackett', to: '/contact', showIcon: true }],
								},
							]}
						/>
					</Col>
				</Row>
			</Container>
		</>
	);
};
